<template>
  <div class="left-0 top-0 absolute w-full h-full flex justify-center items-center">
    <div
      id="building-grid"
      ref="refGrid"
      :style="myStyle"
      class="bg-grid building-grid absolute"
    >
      <div
        class="absolute -right-36 top-0"
      >
        <DisplayModeToggler />
        <OverviewPanel
          class="mt-4"
        />
      </div>
      <GridCell
        v-for="cell in gridCells"
        :key="cell.position + '_' + cell.mode"
        :cell="cell"
      />
      <PendingBuilding
        v-if="pendingBuilding"
        :building-template="pendingBuilding"
        :layout-data="pendingBuildingData"
        :rotate="pendingBuildingRotation"
        @place="onPlace"
      />
      <PlacedBuilding
        v-for="buildingId in placedBuildingsIds"
        :key="buildingId"
        :building-id="buildingId"
        :placing="pendingBuilding !== null"
        @era="onChangeEra"
        @level="onChangeLevel"
        @relocate="onPick"
        @ready="onBuildingReady"
      />
      <div
        v-for="buildingId in placedBuildingsIds"
        :key="buildingId + '_'"
        :data-marked-building="buildingId"
        class="absolute hidden border-2 border-red-600 border-dashed pointer-events-none z-65000"
      />
      <LockedExpansion
        v-for="(tile, id) in lockedExpansions"
        :key="id"
        :expansion-id="id"
        :tile-x="tile.x"
        :tile-y="tile.y"
        @unlock="unlockExpansion"
      />
    </div>
    <ActionBar
      :pending-building="pendingBuilding"
      :placing="pendingBuilding !== null"
      @cancel="cancelPlacement"
      @copy="createNewBasedOnCurrent"
      @duplicate="onDuplicate"
      @levelUp="onLevelUp"
      @lockExpansions="onLockExpansions"
      @remove="removeBuilding"
      @rotate="rotatePickedBuilding"
      @save="generateUrl"
    />
    <LevelUp
      v-if="levelUpActive"
      @close="closeLevelUp"
      @done="levelUpBuildingsAndEra"
    />
  </div>
</template>

<script>
import Grid from '@/roc/grid/Grid';
import ActionBar from '@/components/Grid/ActionBar.vue';
import GridCell from '@/components/Grid/GridCell.vue';
import PendingBuilding from '@/components/Grid/PendingBuilding.vue';
import PlacedBuilding from '@/components/Grid/PlacedBuilding.vue';
import BuildingLevel from '@/roc/building/BuildingLevel';
import DisplayModeToggler from '@/components/Grid/DisplayModeToggler.vue';
import OverviewPanel from '@/components/Grid/OverviewPanel.vue';
import LockedExpansion from "@/components/Grid/LockedExpansion.vue";
import Eras from "@/roc/era";
import LevelUp from "@/components/Modal/LevelUp.vue";
import {computed, nextTick, onMounted, ref, useTemplateRef, watch} from 'vue';
import {useApplicationStore} from '@/roc/stores/ApplicationStore';
import {useExpansionStore} from '@/roc/stores/ExpansionStore';
import {useLayoutStore} from '@/roc/stores/LayoutStore';
import Id from '@/roc/grid/Id';
import setupGrid from '@/components/Grid/Helpers/setupGrid';
import EraType from '@/roc/era/EraType';
import buildingPlacement from '@/components/Grid/Helpers/buildingPlacement';

export default {
  components: {
    LockedExpansion,
    OverviewPanel,
    DisplayModeToggler,
    PlacedBuilding,
    PendingBuilding,
    GridCell,
    ActionBar,
    LevelUp
  },
  emits: ['save', 'reuse','levelUp', 'clear'],
  setup (_, context) {
    const appStore = useApplicationStore();
    const expStore = useExpansionStore();
    const lytStore = useLayoutStore();

    const {
      gridCells,
      buildGrid,
    } = setupGrid();

    const refGrid = ref(null);
    let isInitialized = false;

    const {
      pendingBuilding,
      pendingBuildingData,
      pendingBuildingRotation,

      startAddingNewBuilding,
      pickExistingBuilding,
      rotatePickedBuilding,
      placePickedBuilding,
      duplicateAndPickPickedBuilding,
      removePickedBuilding,
      undoPickBuilding,
    } = buildingPlacement();

    const levelUpActive = ref(false);

    watch(() => appStore.newBuildingTemplateId, (val) => {
      if (val) {
        if (!pendingBuilding.value) {
          nextTick(() => {
            startAddingNewBuilding(appStore.newBuildingTemplateId);
            setupGridForPendingBuilding();
          });
        } else {
          nextTick(() => {
            appStore.clearBuildingCreationQueue();
          });
        }
      }
    });

    const setupGridForPendingBuilding = () => {
      const expansions = document.getElementsByClassName('expansion');
      for (let i = expansions.length - 1; i >= 0; i--) {
        expansions[i].classList.add('pointer-events-none');
      }

      refGrid.value.style.cursor = 'move';
    };

    const cleanupGridFromPendingBuilding = () => {
      const expansions = document.getElementsByClassName('expansion');
      for (let i = expansions.length - 1; i >= 0; i--) {
        expansions[i].classList.remove('pointer-events-none');
      }

      refGrid.value.style.cursor = 'default';

      appStore.clearBuildingCreationQueue();
    };

    onMounted(() => {
      const i = setInterval(() => {
        if (refGrid.value) {
          buildGrid();
          clearInterval(i);
        }
      }, 5);
    });

    return {
      gridCells,
      pendingBuilding,
      pendingBuildingRotation,
      pendingBuildingData,
      levelUpActive,

      refGrid,

      lockedExpansions: computed(() => {
        return expStore.expansions;
      }),
      placedBuildingsIds: computed(() => {
        return lytStore.buildingIds;
      }),

      myStyle: computed(() => {
        if (screen.height >= 800) {
          return [
            'width:' + (Grid.CELL_SIZE * Grid.CELL_COUNT_X) + 'px',
            'height:' + (Grid.CELL_SIZE * Grid.CELL_COUNT_Y) + 'px'
          ].join(';');
        } else {
          return [
            'width:' + (Grid.CELL_SIZE * Grid.CELL_COUNT_X) + 'px',
            'height:' + (Grid.CELL_SIZE * Grid.CELL_COUNT_Y) + 'px'
          ].join(';');
        }
      }),

      rotatePickedBuilding,

      onPlace (element) {
        placePickedBuilding(element);

        cleanupGridFromPendingBuilding();
      },

      onPick (internalId) {
        nextTick(() => {
          pickExistingBuilding(internalId);
          setupGridForPendingBuilding();
        });
      },

      onBuildingReady (buildingId) {
        if (!isInitialized) {
          isInitialized = lytStore.setBuildingReady(buildingId);
        }
      },

      generateUrl () {
        context.emit('save');
      },

      removeBuilding () {
        removePickedBuilding();
        cleanupGridFromPendingBuilding();
      },

      cancelPlacement () {
        undoPickBuilding();
        cleanupGridFromPendingBuilding();
      },

      onChangeEra ({building, era}) {
        lytStore.changeBuildingEra(building, era);
      },

      onChangeLevel ({building, level}) {
        lytStore.changeBuildingLevel(building, level);
      },

      onDuplicate () {
        duplicateAndPickPickedBuilding();
        cleanupGridFromPendingBuilding();

        setTimeout(() => {
          setupGridForPendingBuilding();
        }, 100);
      },

      createNewBasedOnCurrent () {
        context.emit('reuse');
      },

      onLevelUp () {
        levelUpActive.value = true;
      },

      closeLevelUp () {
        levelUpActive.value = false;
      },

      onLockExpansions () {
        const expansions = {};
        const tileOccupancy = {};

        for (let x = 0; x < Grid.CELL_COUNT_X; x++) {
          tileOccupancy[x] = {};

          for (let y = 0; y < Grid.CELL_COUNT_Y; y++) {
            tileOccupancy[x][y] = false;
          }
        }

        lytStore.mapBuildings((building) => {
          const xMax = building.x + (building.isRotated ? building.height : building.width);
          const yMax = building.y + (building.isRotated ? building.width : building.height);

          for (let x = building.x; x < xMax; x++) {
            for (let y = building.y; y < yMax; y++) {
              tileOccupancy[x][y] = true;
            }
          }
        });

        for (let x = 0; x < Grid.CELL_COUNT_X; x += Grid.EXPANSION_SIZE) {
          for (let y = 0; y < Grid.CELL_COUNT_Y; y += Grid.EXPANSION_SIZE) {
            let occupied = false;

            for (let i = x; i < x + Grid.EXPANSION_SIZE; i++) {
              for (let j = y; j < y + Grid.EXPANSION_SIZE; j++) {
                if (tileOccupancy[i][j]) {
                  occupied = true;
                  break;
                }
              }

              if (occupied) {
                break;
              }
            }

            if (!occupied) {
              expansions[Id.generate()] = { x, y };
            }
          }
        }

        expStore.setLockedExpansions(expansions);
      },

      unlockExpansion (expansionId) {
        expStore.unlockExpansion(expansionId);
      },

      levelUpBuildingsAndEra (data) {
        //get name of next era
        const nextEra = Object.keys(Eras)[Object.keys(Eras).indexOf(appStore.selectedEra) + 1];

        //loop through all placed buildings
        lytStore.mapBuildings((building, id) => {
          //always level up city hall
          if (building.isCityHall) {
            lytStore.changeBuildingLevel(id, BuildingLevel.calculateInitialLevel(building.template, nextEra));
          } else {
            //check if the current building type was checked in the modal window -> level up this building
            for (const i in data) {
              if (data[i].name === building.name && data[i].active) {
                lytStore.changeBuildingLevel(id, BuildingLevel.calculateInitialLevel(building.template, nextEra));
                break;
              }
            }
          }
        });

        //increase era
        appStore.changeEra(EraType[nextEra]);
        //close modal window
        levelUpActive.value = false;

        context.emit('levelUp')
      },
    };
  }
};
</script>
